import { useCallback, useState, useMemo } from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
  Polyline,
} from '@react-google-maps/api';
import { MapPin, Navigation, Calendar, X } from 'lucide-react';

// Flexible interface to accept data from various sources
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyItem = any;
interface ItineraryMapProps {
  days: AnyItem[];
  center?: { lat: number; lng: number };
  zoom?: number;
  height?: string;
}

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px',
};

const defaultCenter = {
  lat: 48.8566,
  lng: 2.3522, // Paris
};

const itemTypeColors: Record<string, string> = {
  flight: '#EF4444', // red
  hotel: '#8B5CF6', // purple
  activity: '#10B981', // green
  meal: '#F59E0B', // amber
  transport: '#3B82F6', // blue
  other: '#6B7280', // gray
};

const itemTypeIcons: Record<string, string> = {
  flight: '✈️',
  hotel: '🏨',
  activity: '🎯',
  meal: '🍽️',
  transport: '🚗',
  other: '📍',
};

export default function ItineraryMap({
  days,
  center = defaultCenter,
  zoom = 12,
  height = '400px',
}: ItineraryMapProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  const hasApiKey = Boolean(apiKey && apiKey.length > 0);
  
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: ['places'],
  });

  const [selectedItem, setSelectedItem] = useState<AnyItem | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [showLegend, setShowLegend] = useState(true);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Collect all items with coordinates
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allItems = useMemo(() => {
    return days.flatMap((day: any) =>
      day.items.filter((item: any) => item.latitude && item.longitude)
    );
  }, [days]);

  // Calculate bounds to fit all markers
  const bounds = useMemo(() => {
    if (!isLoaded || allItems.length === 0) return null;

    const bounds = new google.maps.LatLngBounds();
    allItems.forEach((item) => {
      bounds.extend(new google.maps.LatLng(item.latitude!, item.longitude!));
    });
    return bounds;
  }, [isLoaded, allItems]);

  // Fit map to bounds when items change
  const fitToBounds = useCallback(() => {
    if (map && bounds) {
      map.fitBounds(bounds, 50);
    }
  }, [map, bounds]);

  // Create polyline path for route visualization
  const routePath = useMemo(() => {
    return allItems.map((item) => ({
      lat: item.latitude!,
      lng: item.longitude!,
    }));
  }, [allItems]);

  // Show placeholder when no API key is configured
  if (!hasApiKey || loadError) {
    return (
      <div
        style={{ height, borderRadius: '12px' }}
        className="bg-gradient-to-br from-primary-50 to-primary-100 border-2 border-dashed border-primary-200 flex flex-col items-center justify-center p-8"
      >
        <MapPin className="h-16 w-16 text-primary-300 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Interactive Map</h3>
        {loadError ? (
          <p className="text-gray-600 text-center max-w-md mb-4">
            Map temporarily unavailable due to security restrictions. Add VITE_GOOGLE_MAPS_API_KEY to enable.
          </p>
        ) : (
          <p className="text-gray-600 text-center max-w-md mb-4">
            To enable the interactive map with Google Maps, add your API key to the environment variables.
          </p>
        )}
        <div className="bg-white rounded-lg px-4 py-2 text-sm text-gray-500 font-mono">
          VITE_GOOGLE_MAPS_API_KEY=your_api_key
        </div>
        {allItems.length > 0 && (
          <div className="mt-6">
            <p className="text-sm text-gray-600 mb-2">{allItems.length} locations to display:</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {allItems.slice(0, 5).map((item, idx) => (
                <span
                  key={item.id}
                  className="px-3 py-1 bg-white rounded-full text-sm shadow-sm"
                  style={{
                    borderLeft: `4px solid ${itemTypeColors[item.type || item.item_type || 'other']}`
                  }}
                >
                  {idx + 1}. {item.title?.substring(0, 20)}
                </span>
              ))}
              {allItems.length > 5 && (
                <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                  +{allItems.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        style={{ height, borderRadius: '12px' }}
        className="bg-gray-100 flex items-center justify-center"
      >
        <div className="text-center p-4">
          <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">Error loading map</p>
          <p className="text-sm text-gray-400 mt-1">
            Check your Google Maps API key
          </p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{ height, borderRadius: '12px' }}
        className="bg-gray-100 flex items-center justify-center animate-pulse"
      >
        <div className="flex items-center gap-2 text-gray-400">
          <MapPin className="h-5 w-5" />
          <span>Loading map...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Map Controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
        <button
          onClick={fitToBounds}
          className="bg-white p-2 rounded-lg shadow-md hover:bg-gray-50 transition-colors"
          title="Fit all markers"
        >
          <Navigation className="h-5 w-5 text-gray-700" />
        </button>
        <button
          onClick={() => setShowLegend(!showLegend)}
          className={`bg-white p-2 rounded-lg shadow-md transition-colors ${
            showLegend ? 'text-primary-600' : 'text-gray-700'
          }`}
          title="Toggle legend"
        >
          <MapPin className="h-5 w-5" />
        </button>
      </div>

      {/* Day Selector */}
      <div className="mb-4 flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={fitToBounds}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-700 transition-colors"
        >
          <MapPin className="h-4 w-4" />
          Show All ({allItems.length} locations)
        </button>
        {days.map((day: any) => {
          const dayItems = day.items.filter(
            (item: any) => item.latitude && item.longitude
          );
          if (dayItems.length === 0) return null;

          return (
            <button
              key={day.id}
              onClick={() => {
                if (map && dayItems[0]) {
                  map.setCenter({
                    lat: dayItems[0].latitude!,
                    lng: dayItems[0].longitude!,
                  });
                  map.setZoom(14);
                }
              }}
              className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium whitespace-nowrap hover:bg-gray-50 transition-colors"
            >
              Day {day.dayNumber}: {dayItems.length} locations
            </button>
          );
        })}
      </div>

      <GoogleMap
        mapContainerStyle={{ ...mapContainerStyle, height }}
        center={center}
        zoom={zoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }],
            },
          ],
        }}
      >
        {/* Route Polyline */}
        {routePath.length > 1 && (
          <Polyline
            path={routePath}
            options={{
              strokeColor: '#3B82F6',
              strokeOpacity: 0.6,
              strokeWeight: 3,
              geodesic: true,
            }}
          />
        )}

        {/* Markers */}
        {allItems.map((item, index) => {
          const itemType = item.type || item.item_type || 'other';
          return (
            <Marker
              key={item.id}
              position={{ lat: item.latitude!, lng: item.longitude! }}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: itemTypeColors[itemType] || itemTypeColors.other,
                fillOpacity: 1,
                strokeColor: '#FFFFFF',
                strokeWeight: 2,
              }}
              label={{
                text: String(index + 1),
                color: '#FFFFFF',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
              onClick={() => setSelectedItem(item)}
            />
          );
        })}

        {/* Info Window */}
        {selectedItem && (
          <InfoWindow
            position={{
              lat: selectedItem.latitude!,
              lng: selectedItem.longitude!,
            }}
            onCloseClick={() => setSelectedItem(null)}
          >
            <div className="p-2 max-w-xs">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">
                  {itemTypeIcons[
                    (selectedItem.type || selectedItem.item_type || 'other') as string
                  ]}
                </span>
                <span
                  className="text-xs px-2 py-1 rounded-full text-white capitalize"
                  style={{
                    backgroundColor:
                      itemTypeColors[
                        (selectedItem.type || selectedItem.item_type || 'other') as string
                      ],
                  }}
                >
                  {selectedItem.type || selectedItem.item_type || 'other'}
                </span>
              </div>
              <h3 className="font-semibold text-gray-900">{selectedItem.title}</h3>
              {selectedItem.description && (
                <p className="text-sm text-gray-600 mt-1">
                  {selectedItem.description.substring(0, 100)}
                  {selectedItem.description.length > 100 ? '...' : ''}
                </p>
              )}
              {selectedItem.location && (
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                  📍{' '}
                  {typeof selectedItem.location === 'string'
                    ? selectedItem.location
                    : selectedItem.location.name ||
                      selectedItem.location.address ||
                      'Location'}
                </p>
              )}
              {selectedItem.start_time && (
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {selectedItem.start_time ? new Date(selectedItem.start_time).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  }) : ''}
                  {selectedItem.end_time &&
                    ` - ${selectedItem.end_time ? new Date(selectedItem.end_time).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    }) : ''}`}
                </p>
              )}
              {selectedItem.price !== undefined && selectedItem.price > 0 && (
                <p className="text-sm font-medium text-primary-600 mt-1">
                  💰 {selectedItem.price}{' '}
                  {selectedItem.currency || 'USD'}
                </p>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Legend */}
      {showLegend && (
        <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900">Map Legend</h4>
            <button
              onClick={() => setShowLegend(false)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            {Object.entries(itemTypeIcons).map(([type, icon]) => (
              <div key={type} className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: itemTypeColors[type],
                  }}
                ></span>
                <span className="text-gray-600 capitalize flex items-center gap-1">
                  <span>{icon}</span>
                  <span>{type}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      {allItems.length === 0 && (
        <div className="mt-4 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
          <p className="text-sm text-yellow-800">
            No locations with coordinates found. Add activities with location data
            to see them on the map.
          </p>
        </div>
      )}
    </div>
  );
}
